/* eslint-disable no-console */
import pino from 'pino';
import { P, match } from 'ts-pattern';
import type { StrictExclude } from '../types';

/**
 * Available log levels in order of increasing severity.
 * - debug: Detailed information used for debugging
 * - info: General information about normal operation
 * - warn: Potential issues that aren't errors
 * - error: Error conditions that may allow the application to continue
 * - fatal: Severe errors that typically cause the application to terminate
 */
export type LogLevel = 'debug' | 'error' | 'fatal' | 'info' | 'warn';

/**
 * Configuration options for the logger system.
 * Controls behavior including output formatting, filtering, and annotations.
 */
export type LoggerConfig = {
  /** Additional contextual information to include with all logs from this logger */
  annotations?: Record<string, unknown>;

  /**
   * When true, produces more compact log output by omitting timestamp
   * Useful for environments that already timestamp logs
   */
  compactFormat?: boolean;

  /**
   * Default minimum log level, used as initial setting for `level`
   */
  defaultLevel?: LogLevel;

  /** Whether console output is enabled */
  enableConsole: boolean;

  /** Include log level in output (DEBUG, INFO, etc.) */
  enableLogLevel: boolean;

  /** Include source code location in log output */
  enableSource: boolean;

  /** Include ISO timestamp in log output */
  enableTimestamp: boolean;

  /**
   * List of scopes that should be displayed in logs.
   * Logs from scopes not in this list will be filtered out.
   * Include 'unscoped' to show logs without a specified scope.
   * When used in a specific app, this will be a list of strictly typed string enums.
   */
  enabledScopes?: string[];

  /**
   * Minimum log level to display. Logs below this level are filtered out.
   * Levels increase in severity: debug < info < warn < error < fatal
   */
  level: LogLevel;
};

/**
 * Default configuration applied to all loggers unless overridden.
 * By default enables all output features and sets a reasonable
 * minimum log level.
 */
export const defaultConfig = {
  annotations: {},
  compactFormat: false,
  defaultLevel: 'debug',
  enableConsole: true,
  enableLogLevel: true,
  enableSource: true,
  enableTimestamp: true,
  enabledScopes: ['unscoped'],
  level: 'debug',
} satisfies LoggerConfig;

const formatLog = (level: string, message: unknown, args: unknown[], config: LoggerConfig): string => {
  const timestamp = !config.compactFormat && config.enableTimestamp ? `[${new Date().toISOString()}]` : '';
  const logLevel = config.enableLogLevel ? `[${level.toUpperCase()}]` : '';

  const annotations = (() => {
    if (config.annotations && Object.keys(config.annotations).length > 0) {
      return match(config.annotations)
        .with({ scope: P.string }, ({ scope, ...restAnnotations }) =>
          Object.keys(restAnnotations).length
            ? `[SCOPE: ${scope}] [${JSON.stringify(restAnnotations)}]`
            : `[SCOPE: ${scope}]`,
        )
        .otherwise(() => '');
    }
    return '';
  })();

  const messageStr = typeof message === 'object' ? JSON.stringify(message) : message;
  const argsStr =
    args.length > 0 ? args.map((arg) => (typeof arg === 'object' ? JSON.stringify(arg) : arg)).join(' ') : '';

  return [timestamp, logLevel, annotations, messageStr, argsStr].filter(Boolean).join(' ');
};

// Check if a log should be displayed based on scope
const shouldLogScope = (scope: MaybeUndefined<string>, config: LoggerConfig): boolean => {
  if (!scope) {
    return config.enabledScopes?.includes('unscoped') || false;
  }
  return config.enabledScopes?.includes(scope) || false;
};

// Helper to extract scope from config
const getScope = (config: LoggerConfig): MaybeUndefined<string> => {
  return config.annotations && 'scope' in config.annotations ? (config.annotations['scope'] as string) : undefined;
};

// Helper to check if a log level is enabled
const isLevelEnabled = (level: LogLevel, configLevel: LogLevel): boolean => {
  const levels = ['debug', 'info', 'warn', 'error', 'fatal'] as const satisfies LogLevel[];
  const levelIndex = levels.indexOf(level);
  const configLevelIndex = levels.indexOf(configLevel);
  return levelIndex >= configLevelIndex;
};

// Simplified log writer function shared across all log levels
const createLogWriter = (
  level: LogLevel,
  consoleMethod: StrictExclude<LogLevel, 'fatal'>,
  config: LoggerConfig,
): pino.WriteFn => {
  return (o) => {
    if (!config.enableConsole) return;

    // Check scope filtering
    const scope = match(o)
      .with({ scope: P.string }, ({ scope }) => scope)
      .with({ msg: { scope: P.string } }, ({ msg: { scope } }) => scope)
      .otherwise(() => getScope(config));

    if (!shouldLogScope(scope, config)) return;

    // Check log level
    if (!isLevelEnabled(level, config.level)) return;

    const formatted = formatLog(level, 'msg' in o ? o.msg : o, [], config);
    console[consoleMethod](formatted);
  };
};

const createPinoOptions = (config: LoggerConfig): Parameters<typeof pino>[0] => {
  const browserFormatter = {
    bindings: () => ({}),
    level: (label: string) => ({ level: label }),
    log: () => ({}),
  };

  // Create all log level writers using the shared function
  const writers = {
    debug: createLogWriter('debug', 'debug', config),
    error: createLogWriter('error', 'error', config),
    fatal: createLogWriter('fatal', 'error', config),
    info: createLogWriter('info', 'info', config),
    warn: createLogWriter('warn', 'warn', config),
  } satisfies Record<LogLevel, pino.WriteFn>;

  return {
    base: config.annotations || {},
    browser: {
      asObject: false,
      write: writers,
    },
    formatters: browserFormatter,
    level: config.defaultLevel || 'debug',
  };
};

/**
 * Creates a logger factory that produces loggers with the specified configuration.
 * This factory can then be used to create base loggers and scoped loggers throughout
 * an application, ensuring consistent configuration.
 *
 * @example
 * // Create a logger factory for an application
 * const { logger, getScopedLogger } = createLoggerFactory({
 *   enabledScopes: ['unscoped', 'API', 'Auth'],
 *   defaultLevel: process.env.NODE_ENV === 'production' ? 'info' : 'debug',
 *   compactFormat: true
 * });
 *
 * // Use the base logger
 * logger.info('Application started');
 *
 * // Use a scoped logger
 * const apiLogger = getScopedLogger('API');
 * apiLogger.debug('Processing request', { endpoint: '/users' });
 *
 * @param appConfig - Configuration options for all loggers created by this factory
 * @param appConfig.enabledScopes - Array of scope names that should be displayed in logs
 * @param appConfig.defaultLevel - Default minimum log level (debug, info, warn, error, fatal)
 * @param appConfig.compactFormat - When true, produces more compact log output (no timestamp)
 *
 * @returns An object containing:
 *   - logger: A base logger instance with the specified configuration
 *   - createLogger: Function to create custom loggers with additional configuration
 *   - getScopedLogger: Function to create a logger for a specific scope
 */
export const createLoggerFactory = <TScope extends string>(
  appConfig: {
    compactFormat?: boolean;
    defaultLevel?: LogLevel;
    enabledScopes?: TScope[];
  } = {},
) => {
  // Create base configuration by merging defaults with app config
  const baseConfig: LoggerConfig = {
    ...defaultConfig,
    ...appConfig,
  };

  // Helper function to merge configs while preserving annotations
  const mergeConfigs = (
    base: LoggerConfig,
    override?: Partial<LoggerConfig>,
    extraAnnotations?: Record<string, unknown>,
  ) => {
    return {
      ...base,
      ...override,
      annotations: {
        ...base.annotations,
        ...override?.annotations,
        ...extraAnnotations,
      },
      // Ensure level is set from defaultLevel if not explicitly provided
      level: override?.level || override?.defaultLevel || base.level,
    };
  };

  // Create base logger
  const baseLogger = pino(createPinoOptions(baseConfig));

  /**
   * Creates a customized logger with the specified configuration.
   *
   * @example
   * const debugLogger = createLogger({
   *   level: 'debug',
   *   annotations: { component: 'AuthService' }
   * });
   * debugLogger.debug('Detailed auth debug info');
   *
   * @param config - Additional configuration to override the base settings
   * @returns A new logger instance with the merged configuration
   */
  const createLogger = (config: Partial<LoggerConfig> = {}) =>
    pino(createPinoOptions(mergeConfigs(baseConfig, config)));

  /**
   * Creates a logger for a specific scope.
   * The scope is included in all log messages from this logger,
   * and it's used for filtering based on the enabledScopes setting.
   *
   * @example
   * const userLogger = getScopedLogger('UserService');
   * userLogger.info('User logged in', { userId: '123' });
   *
   * @param scope - The scope name for this logger
   * @param config - Additional configuration to override the base settings
   * @returns A new logger instance for the specified scope with the merged configuration
   */
  const getScopedLogger = <TScope extends string>(scope: TScope, config: Partial<LoggerConfig> = {}) =>
    pino(createPinoOptions(mergeConfigs(baseConfig, config, { scope })));

  return {
    createLogger,
    getScopedLogger,
    logger: baseLogger,
  };
};

/**
 * Logger interface that extends the Pino logger.
 * Provides methods for each log level and inherits the Pino logger functionality.
 *
 * @example
 * const logger: Logger = getScopedLogger('MyScope');
 * logger.info('Information message');
 * logger.error('Error occurred', new Error('Details'));
 */
export type Logger = pino.Logger;
